// imports
import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, uiActions } from '../store';

// local imports
import { useGetAWDConfigQuery } from '../services/awdVizConfigApi';
import { months } from '../constants/Months';

const getMonthFullName = (monthValue: string): string => {
  const monthEntry = months.find((m) => m.value === monthValue);
  return monthEntry ? monthEntry.value : monthValue;
};

const cloudfrontPrefix = import.meta.env.DEV ? `${import.meta.env.VITE_NWDC_S3_URL}` : import.meta.env.VITE_CLOUDFRONT_PROXY_PREFIX;

/**
 * useAwdLegend Hook
 *
 * Manages the Available Water Data (AWD) legend in the Redux store.
 * Automatically adds, updates, and removes legend content.
 *
 * Behavior:
 * - Fetches AWD legend attributes when the AWD layer is visible
 * - Updates legend content when a new AWD layer is displayed
 * - Removes legend when AWD layer is hidden
 * - Cleans up legend on component unmount
 *
 * @hook
 * @returns {void} This hook manages side effects and does not return a value
 */
export default function useAwdLegend() {
  const dispatch = useDispatch();

  // Check if AWD layer is currently visible
  const awdVisible = useSelector(
    (appState: RootState) => appState.map.awdVisible
  );

  // Check if no-data state is active
  const awdNoData = useSelector((appState: RootState) => appState.ui.awdNoData);

  // Check if no-data probe is still running
  const awdNoDataProbing = useSelector(
    (appState: RootState) => appState.ui.awdNoDataProbing
  );

  // Get currently applied model, variable, month, and year
  const {
    appliedSector,
    appliedModel,
    appliedVariable,
    appliedStartMonth,
    appliedStartYear,
  } = useSelector((state: RootState) => state.waterData);

  // Track whether legend has been added to store
  const addedRef = useRef(false);

  // Fetch the AWD config to get var_name, colors, and bin_labels
  const { data: awdConfig } = useGetAWDConfigQuery(
    {
      model: appliedModel ?? 'wu-public-supply-cu',
      variable: appliedVariable ?? 'pscutot',
    },
  );

  useEffect(() => {
    // Remove legend when no data is available
    if (awdNoData && addedRef.current) {
      dispatch(uiActions.removeLegendContent('awd-legend'));
      addedRef.current = false;
      return;
    }

    // Remove legend when awdVisible is false
    if (!awdVisible && addedRef.current) {
      dispatch(uiActions.removeLegendContent('awd-legend'));
      addedRef.current = false;
    }

    // Do not add/update legend while probe is running or if there are no data
    if (awdNoData || awdNoDataProbing) return;

    // Add legend when awdVisible is true and we have the config data
    if (awdVisible && awdConfig && !addedRef.current) {
      const legendContent = {
        id: 'awd-legend',
        name: awdConfig.var_name,
        units: awdConfig.units,
        colors: awdConfig.colors,
        binLabels: awdConfig.bin_labels,
        month: appliedStartMonth ? getMonthFullName(appliedStartMonth) : '',
        year: appliedStartYear || '',
        variableId: appliedVariable || '',
        variableLink: `${cloudfrontPrefix}/data-catalog/sector/${appliedSector}/model/${appliedModel}/variable/${appliedVariable}/#variableDescription`,
      };

      dispatch(uiActions.addLegendContent(legendContent));
      addedRef.current = true;
    }

    // Update legend if config changes while visible
    if (awdVisible && awdConfig && addedRef.current) {
      const legendContent = {
        id: 'awd-legend',
        name: awdConfig.var_name,
        units: awdConfig.units,
        colors: awdConfig.colors,
        binLabels: awdConfig.bin_labels,
        month: appliedStartMonth ? getMonthFullName(appliedStartMonth) : '',
        year: appliedStartYear || '',
        variableId: appliedVariable || '',
        variableLink: `${cloudfrontPrefix}/data-catalog/sector/${appliedSector}/model/${appliedModel}/variable/${appliedVariable}/#variableDescription`,
      };

      // Remove old legend and add new legend to update the content
      dispatch(uiActions.removeLegendContent('awd-legend'));
      dispatch(uiActions.addLegendContent(legendContent));
    }
  }, [awdNoData, awdNoDataProbing, awdVisible, awdConfig, appliedStartMonth, appliedStartYear, appliedSector, appliedModel, appliedVariable, dispatch]);

  // Cleanup on unmount - remove the legend if it was added
  useEffect(() => {
    return () => {
      if (addedRef.current) {
        dispatch(uiActions.removeLegendContent('awd-legend'));
        addedRef.current = false;
      }
    };
  }, [dispatch]);
}
